/* ============================================================================
   Marenia · Custom Product Section · JavaScript
   ----------------------------------------------------------------------------
   - One <product-marenia> custom element owns the whole section.
   - Modules: Gallery, VariantSelector, BundleSelector, SizeGuide,
              StickyBar, AccordionsNative, Toast, Countdown.
   - Uses Shopify AJAX cart API (/cart/add.js).
   - No external deps; ~7 KB minified.
   ============================================================================ */
(function () {
  'use strict';

  /* ─────────────── helpers ─────────────── */
  const $  = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts || false);
  const formatMoney = (cents) => {
    if (window.Shopify && Shopify.formatMoney && window.theme?.moneyFormat) {
      return Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    // sensible fallback — EUR with comma decimal
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* ─────────────── custom element ─────────────── */
  class ProductMarenia extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      // Parse product JSON
      try {
        const jsonEl = $('[data-product-json]', this);
        this.product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      } catch (e) {
        console.warn('[Marenia] failed to parse product JSON', e);
        this.product = null;
      }

      this.gallery   = new Gallery(this);
      this.variants  = new VariantSelector(this);
      this.bundles   = new BundleSelector(this);
      this.modal     = new SizeGuide(this);
      this.sticky    = new StickyBar(this);
      this.toast     = new Toast(this);
      new Countdowns(this);

      // Add-to-cart
      const form = $('[data-product-form]', this);
      if (form) on(form, 'submit', (e) => this.onAddToCart(e));
    }

    onAddToCart(e) {
      e.preventDefault();
      const form = e.currentTarget;
      const btn  = $('[data-add-to-cart]', this);
      if (!btn || btn.classList.contains('is-loading')) return;

      btn.classList.add('is-loading');
      btn.disabled = true;

      // Bundle: build line items array
      const bundleItems = this.bundles.buildCartItems();
      const items = bundleItems.length ? bundleItems : [{
        id: $('[data-variant-id-input]', form).value,
        quantity: 1,
      }];

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then(r => r.ok ? r.json() : r.json().then(err => { throw err; }))
        .then(data => {
          this.toast.show({
            title: this.dataset.cartToastTitle || 'Añadido al carrito',
            sub: items.length > 1
              ? `${items.length} artículos`
              : (data.items?.[0]?.title || this.product?.title || ''),
          });
          this.refreshCartCount();
          if (this.dataset.cartRedirect === 'true') {
            window.location.href = '/cart';
          }
        })
        .catch(err => {
          console.error('[Marenia] add to cart failed', err);
          this.toast.show({
            title: err?.description || err?.message || 'Error',
            sub: '',
            error: true,
          });
        })
        .finally(() => {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
    }

    refreshCartCount() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
        })
        .catch(() => {});
    }
  }

  /* ─────────────── gallery ─────────────── */
  class Gallery {
    constructor(root) {
      this.root    = root;
      this.track   = $('[data-gallery-track]', root);
      this.dots    = $$('[data-gallery-dot]', root);
      this.current = $('[data-gallery-current]', root);
      this.total   = $('[data-gallery-total]', root);
      this.zoom    = $('[data-gallery-zoom]', root);
      this.slides  = $$('[data-gallery-slide]', root);
      if (!this.track || !this.slides.length) return;

      if (this.total) this.total.textContent = String(this.slides.length).padStart(2, '0');

      on(this.track, 'scroll', () => this.onScroll(), { passive: true });
      this.dots.forEach(dot => on(dot, 'click', () => this.go(parseInt(dot.dataset.index, 10))));
      if (this.zoom) on(this.zoom, 'click', () => this.openZoom());

      this.update(0);
    }
    onScroll() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        const idx = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (idx !== this._idx) this.update(idx);
      });
    }
    go(idx) {
      if (idx < 0 || idx >= this.slides.length) return;
      this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: 'smooth' });
    }
    goByMediaId(id) {
      const i = this.slides.findIndex(s => s.dataset.mediaId === String(id));
      if (i >= 0) this.go(i);
    }
    update(idx) {
      this._idx = idx;
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      if (this.current) this.current.textContent = String(idx + 1).padStart(2, '0');
    }
    openZoom() {
      // Find a target slide to enlarge
      const slide = this.slides[this._idx];
      const img   = slide && slide.querySelector('img');
      if (!img) return;
      const lb = document.createElement('div');
      lb.className = 'mar-lightbox';
      lb.style.cssText = 'position:fixed;inset:0;z-index:100;background:rgba(14,14,14,.92);display:flex;align-items:center;justify-content:center;padding:24px;cursor:zoom-out;';
      const big = img.cloneNode();
      big.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;';
      // upgrade to a larger source if possible
      if (img.src.includes('width=')) {
        big.src = img.src.replace(/width=\d+/, 'width=2000');
      }
      lb.appendChild(big);
      on(lb, 'click', () => lb.remove());
      document.body.appendChild(lb);
    }
  }

  /* ─────────────── variant selector ─────────────── */
  class VariantSelector {
    constructor(root) {
      this.root    = root;
      this.product = root.product;
      this.form    = $('[data-product-form]', root);
      if (!this.product || !this.form) return;
      this.idInput = $('[data-variant-id-input]', this.form);

      // current selection map: position -> value
      this.selected = {};
      this.product.options.forEach((opt, i) => {
        const v = this.product.selected_or_first_available_variant || this.product.variants[0];
        if (v && v.options) this.selected[i + 1] = v.options[i];
      });

      $$('[data-option-input]', root).forEach(btn => {
        on(btn, 'click', () => this.choose(btn));
      });
    }

    choose(btn) {
      if (btn.disabled) return;
      const pos = parseInt(btn.dataset.optionPosition, 10);
      const val = btn.dataset.optionValue;
      this.selected[pos] = val;

      // Toggle active state in group
      const group = btn.parentElement;
      $$('[data-option-input]', group).forEach(b => {
        const isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      // Update label
      const head = btn.closest('.mar-option');
      const display = head && head.querySelector('[data-option-value-display]');
      if (display) display.textContent = val;

      this.resolveVariant();
    }

    resolveVariant() {
      const matched = this.product.variants.find(v =>
        v.options.every((o, i) => o === this.selected[i + 1])
      );

      if (!matched) return;
      if (this.idInput) this.idInput.value = matched.id;

      // Sync URL (non-destructive)
      try {
        const u = new URL(window.location);
        u.searchParams.set('variant', matched.id);
        history.replaceState({}, '', u.toString());
      } catch (e) { /* ignore */ }

      // Sync price
      const priceEl   = $('[data-price]', this.root);
      const compareEl = $('[data-compare-price]', this.root);
      const savingsEl = $('[data-savings]', this.root);
      const cartPrice = $('[data-cart-price]', this.root);
      const stickyP   = $('[data-sticky-price]', this.root);
      const stickyC   = $('[data-sticky-compare]', this.root);

      if (priceEl) priceEl.textContent = formatMoney(matched.price);
      if (cartPrice) cartPrice.textContent = formatMoney(matched.price);
      if (stickyP) stickyP.textContent = formatMoney(matched.price);

      if (matched.compare_at_price && matched.compare_at_price > matched.price) {
        if (compareEl) {
          compareEl.textContent = formatMoney(matched.compare_at_price);
          compareEl.hidden = false;
        }
        if (stickyC) {
          stickyC.textContent = formatMoney(matched.compare_at_price);
          stickyC.hidden = false;
        }
        if (savingsEl) {
          const saved = matched.compare_at_price - matched.price;
          const pct = Math.round((saved / matched.compare_at_price) * 100);
          savingsEl.textContent = `−${pct}% · ${formatMoney(saved)}`;
          savingsEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (stickyC) stickyC.hidden = true;
        if (savingsEl) savingsEl.hidden = true;
      }

      // Sync availability
      const btn = $('[data-add-to-cart]', this.root);
      if (btn) {
        if (matched.available) {
          btn.disabled = false;
          const text = $('[data-cart-text-default]', btn);
          if (text && !text.dataset.original) text.dataset.original = text.textContent;
        } else {
          btn.disabled = true;
          const text = $('[data-cart-text-default]', btn);
          if (text) text.textContent = 'AGOTADO';
        }
      }

      // Sync gallery (if variant has a featured_media id)
      if (matched.featured_media && this.root.dataset && this.root.gallery) {
        this.root.gallery.goByMediaId(matched.featured_media.id);
      } else if (matched.featured_media) {
        const g = this.root.gallery || (this.root.querySelector('product-marenia')?.gallery);
        if (g) g.goByMediaId(matched.featured_media.id);
      }
      // Fallback: walk up to host
      const host = this.root.closest('product-marenia') || this.root;
      if (host.gallery && matched.featured_media) {
        host.gallery.goByMediaId(matched.featured_media.id);
      }
    }
  }

  /* ─────────────── bundle selector ─────────────── */
  class BundleSelector {
    constructor(root) {
      this.root    = root;
      this.options = $$('[data-bundle-option]', root);
      this.slots   = $$('[data-bundle-slot]', root);
      this.current = 'two';

      this.options.forEach(opt => on(opt, 'click', () => this.select(opt.dataset.bundleId)));
      // initial
      const active = this.options.find(o => o.classList.contains('is-active'));
      if (active) this.select(active.dataset.bundleId, true);
    }
    select(id, silent) {
      this.current = id;
      this.options.forEach(o => {
        const isActive = o.dataset.bundleId === id;
        o.classList.toggle('is-active', isActive);
        o.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
      const qty = id === 'one' ? 1 : id === 'two' ? 2 : 3;
      this.slots.forEach(slot => {
        const slotN = parseInt(slot.dataset.bundleSlot, 10);
        slot.hidden = slotN > qty;
      });
    }
    buildCartItems() {
      const active = this.options.find(o => o.classList.contains('is-active'));
      if (!active) return [];
      const qty = active.dataset.bundleId === 'one' ? 1
                : active.dataset.bundleId === 'two' ? 2
                : 3;
      const linked = active.dataset.bundleProduct;
      const gift   = active.dataset.bundleGiftProduct;
      const variantId = $('[data-variant-id-input]', this.root)?.value;
      const items = [];
      // Main variant qty
      if (variantId) items.push({ id: variantId, quantity: qty });
      // Free gift
      if (gift) items.push({ id: gift, quantity: 1, properties: { _bundle_gift: 'true' } });
      // If no bundle, fall back to standard add-to-cart
      if (qty === 1 && !gift) return [];
      return items;
    }
  }

  /* ─────────────── size guide modal ─────────────── */
  class SizeGuide {
    constructor(root) {
      this.modal = $('[data-size-guide-modal]', root);
      if (!this.modal) return;
      $$('[data-size-guide-open]', root).forEach(b => on(b, 'click', () => this.open()));
      $$('[data-size-guide-close]', root).forEach(b => on(b, 'click', () => this.close()));
      on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() {
      if (!this.modal) return;
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      const closeBtn = $('.mar-modal__close', this.modal);
      closeBtn && closeBtn.focus();
    }
    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.style.overflow = '';
    }
  }

  /* ─────────────── sticky bottom CTA ─────────────── */
  class StickyBar {
    constructor(root) {
      this.root  = root;
      this.bar   = $('[data-sticky-bar]', root);
      if (!this.bar) return;

      const cta = $('[data-sticky-cta]', this.bar);
      const form = $('[data-product-form]', root);
      if (cta && form) on(cta, 'click', () => form.requestSubmit());

      this.threshold = 200;
      on(window, 'scroll', () => this.update(), { passive: true });
      this.update();
    }
    update() {
      if (!this.bar) return;
      const show = window.scrollY > this.threshold;
      this.bar.hidden = !show;
    }
  }

  /* ─────────────── toast ─────────────── */
  class Toast {
    constructor(root) {
      this.toast = $('[data-cart-toast]', root);
    }
    show({ title, sub, error }) {
      if (!this.toast) return;
      const t = $('.mar-toast__title', this.toast);
      const s = $('[data-toast-sub]', this.toast);
      if (t) t.textContent = title || '';
      if (s) s.textContent = sub  || '';
      this.toast.style.background = error ? '#8A0F1F' : '';
      this.toast.hidden = false;
      clearTimeout(this._timer);
      this._timer = setTimeout(() => { this.toast.hidden = true; }, 2400);
    }
  }

  /* ─────────────── countdown ─────────────── */
  class Countdowns {
    constructor(root) {
      this.targets = $$('[data-countdown]', root);
      if (!this.targets.length) return;
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }
    tick() {
      const now = Date.now();
      this.targets.forEach(el => {
        const to = new Date(el.dataset.countdown).getTime();
        const ms = to - now;
        if (Number.isNaN(to) || ms <= 0) { el.textContent = '00:00:00'; return; }
        const h = Math.floor(ms / 3.6e6);
        const m = Math.floor((ms % 3.6e6) / 6e4);
        const s = Math.floor((ms % 6e4) / 1e3);
        el.textContent = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      });
    }
  }

  /* ─────────────── register ─────────────── */
  if (!customElements.get('product-marenia')) {
    customElements.define('product-marenia', ProductMarenia);
  }
})();
